import React, { useState, useEffect, useMemo } from 'react';
import { Modal } from '../ui/Modal';
import { Button } from '@smartphone-tycoon/shared-ui';
import { EMPLOYEE_CONSTANTS } from '../../config/gameConstants';
import type { PlannedEmployeeAllocations } from '../../types/product';
import type { Employee } from '../../types/tycoon';

interface EmployeeAllocationModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (allocations: PlannedEmployeeAllocations) => void;
  employees: Employee[];
  currentAllocations?: Array<{
    productId: string;
    phase: string;
    employeeType: string;
    count: number;
  }>;
  productName: string;
  techScore: number;
  currentYear: number;
}

interface AvailableEmployees {
  designer: number;
  engineer: number;
  tester: number;
  assemblyWorker: number;
}

interface RequiredEmployees {
  [phase: string]: {
    [role: string]: number;
  };
}

export function EmployeeAllocationModal({
  isOpen,
  onClose,
  onConfirm,
  employees,
  currentAllocations = [],
  productName,
  techScore,
  currentYear
}: EmployeeAllocationModalProps) {
  const [allocations, setAllocations] = useState<PlannedEmployeeAllocations>({
    design: {},
    engineering: {},
    testing: {},
    manufacturing: {},
    production: {}
  });
  
  const [errors, setErrors] = useState<Record<string, string>>({});

  // Debug logging
  // useEffect(() => {
  //   console.log('[EmployeeAllocationModal] Props received:', {
  //     currentAllocations,
  //     employees: employees.length,
  //     productName,
  //     isOpen
  //   });
  // }, [currentAllocations, employees, productName, isOpen]);

  // Calculate available employees by type - memoized to prevent recreating on every render
  const available = useMemo((): AvailableEmployees => {
    const available: AvailableEmployees = {
      designer: 0,
      engineer: 0,
      tester: 0,
      assemblyWorker: 0
    };

    // Count total employees by role
    employees.forEach(emp => {
      if (emp.role in available) {
        available[emp.role as keyof AvailableEmployees]++;
      }
    });

    // Subtract already allocated employees
    // console.log('[EmployeeAllocationModal] Processing current allocations:', currentAllocations);
    currentAllocations.forEach(allocation => {
      // Map employeeType to role names used in available object
      const roleMap: Record<string, keyof AvailableEmployees> = {
        designer: 'designer',
        engineer: 'engineer', 
        tester: 'tester',
        assemblyWorker: 'assemblyWorker'
      };
      
      const role = roleMap[allocation.employeeType];
      if (role && role in available) {
        // console.log(`[EmployeeAllocationModal] Subtracting ${allocation.count} ${role} employees (allocated to product ${allocation.productId}, phase ${allocation.phase})`);
        available[role] = Math.max(0, available[role] - allocation.count);
      }
    });
    // console.log('[EmployeeAllocationModal] Final available employees:', available);
    return available;
  }, [employees, currentAllocations]);

  // Calculate required employees based on tech score and year - memoized to prevent recreating on every render
  const required = useMemo((): RequiredEmployees => {
    const { minEmployeesPerPhase, employeeScaling } = EMPLOYEE_CONSTANTS;
    
    // Calculate scaling factors
    const techMultiplier = Math.min(
      employeeScaling.techComplexityBase + (techScore * employeeScaling.techComplexityFactor),
      employeeScaling.maxTechMultiplier
    );
    
    const yearsSince2000 = currentYear - 2000;
    const timeMultiplier = Math.min(
      employeeScaling.timeProgressionBase + (yearsSince2000 * employeeScaling.timeProgressionFactor),
      employeeScaling.maxTimeMultiplier
    );
    
    const totalMultiplier = techMultiplier * timeMultiplier;

    // Calculate required employees for each phase
    const required: RequiredEmployees = {};
    
    Object.entries(minEmployeesPerPhase).forEach(([phase, roles]) => {
      // Skip production phase - assembly workers are allocated to production lines, not products
      if (phase === 'production') {
        required[phase] = {};
        return;
      }
      
      required[phase] = {};
      Object.entries(roles).forEach(([role, baseCount]) => {
        required[phase][role] = Math.ceil(baseCount * totalMultiplier);
      });
    });

    return required;
  }, [techScore, currentYear]);

  // Initialize allocations based on requirements
  useEffect(() => {
    const initialAllocations: PlannedEmployeeAllocations = {
      design: {},
      engineering: {},
      testing: {},
      manufacturing: {},
      production: {}
    };

    Object.entries(required).forEach(([phase, roles]) => {
      Object.entries(roles).forEach(([role, count]) => {
        initialAllocations[phase as keyof PlannedEmployeeAllocations][role] = count;
      });
    });

    setAllocations(initialAllocations);
  }, [isOpen, required]); // Reset when modal opens or requirements change

  const handleAllocationChange = (phase: string, role: string, value: string) => {
    const numValue = parseInt(value) || 0;
    setAllocations(prev => ({
      ...prev,
      [phase]: {
        ...prev[phase as keyof PlannedEmployeeAllocations],
        [role]: numValue
      }
    }));
    
    // Clear error for this field
    setErrors(prev => {
      const newErrors = { ...prev };
      delete newErrors[`${phase}-${role}`];
      return newErrors;
    });
  };

  const validateAllocations = (): boolean => {
    const newErrors: Record<string, string> = {};
    const totalAllocated: Record<string, number> = {
      designer: 0,
      engineer: 0,
      tester: 0,
      assemblyWorker: 0
    };

    // Count total allocations per role
    Object.entries(allocations).forEach(([phase, roles]) => {
      Object.entries(roles).forEach(([role, count]) => {
        totalAllocated[role] += count;
        
        // Check if allocated is less than required
        const requiredCount = required[phase]?.[role] || 0;
        if (count < requiredCount) {
          newErrors[`${phase}-${role}`] = `Minimum ${requiredCount} required`;
        }
      });
    });

    // Check if total allocations exceed available
    Object.entries(totalAllocated).forEach(([role, total]) => {
      if (total > available[role as keyof AvailableEmployees]) {
        newErrors[`total-${role}`] = `Only ${available[role as keyof AvailableEmployees]} ${role}s available`;
      }
    });

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleConfirm = () => {
    if (validateAllocations()) {
      onConfirm(allocations);
    }
  };

  // Check if current allocations are valid - memoized to prevent recalculating on every render
  const isValid = useMemo(() => {
    const totalAllocated: Record<string, number> = {
      designer: 0,
      engineer: 0,
      tester: 0,
      assemblyWorker: 0
    };

    // Count total allocations per role
    let hasErrors = false;
    Object.entries(allocations).forEach(([phase, roles]) => {
      Object.entries(roles).forEach(([role, count]) => {
        totalAllocated[role] += count;
        
        // Check if allocated is less than required
        const requiredCount = required[phase]?.[role] || 0;
        if (count < requiredCount) {
          hasErrors = true;
        }
      });
    });

    // Check if total allocations exceed available
    Object.entries(totalAllocated).forEach(([role, total]) => {
      if (total > available[role as keyof AvailableEmployees]) {
        hasErrors = true;
      }
    });

    return !hasErrors;
  }, [allocations, required, available]);

  const phases = ['design', 'engineering', 'testing', 'manufacturing'] as const;
  const roleNames: Record<string, string> = {
    designer: 'Designers',
    engineer: 'Engineers',
    tester: 'Testers',
    assemblyWorker: 'Assembly Workers'
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Allocate Employees" size="xl">
      <div className="space-y-4">
        <div className="bg-theme-bg-secondary border-l-4 border-theme-accent rounded-lg p-4">
          <h4 className="font-medium text-theme-text-primary mb-2">Product: {productName}</h4>
          <p className="text-sm text-theme-text-secondary">
            Tech Score: {techScore.toFixed(1)} | Year: {currentYear}
          </p>
          <p className="text-sm text-theme-text-secondary mt-1">
            Allocate employees to each development phase. Higher tech products require more employees.
          </p>
        </div>

        {/* Available employees summary */}
        <div className="bg-theme-bg-secondary border border-theme-border rounded-lg p-4">
          <h4 className="font-medium text-theme-text-primary mb-2">Available Employees</h4>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-sm">
            {Object.entries(available).map(([role, count]) => (
              <div key={role}>
                <span className="text-theme-text-secondary">{roleNames[role]}:</span>
                <span className="ml-2 font-medium">{count}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Allocation inputs */}
        <div className="space-y-4">
          {phases.map(phase => {
            const phaseRoles = Object.keys(required[phase] || {});
            if (phaseRoles.length === 0) return null;

            return (
              <div key={phase} className="border border-theme-border rounded-lg p-4">
                <h4 className="font-medium text-theme-text-primary mb-3 capitalize">{phase} Phase</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {phaseRoles.map(role => {
                    const requiredCount = required[phase][role];
                    const allocatedCount = allocations[phase][role] || 0;
                    const hasError = errors[`${phase}-${role}`];

                    return (
                      <div key={role}>
                        <label className="block text-sm font-medium text-theme-text-secondary mb-1">
                          {roleNames[role]} (min: {requiredCount})
                        </label>
                        <div className="flex items-center space-x-2">
                          <input
                            type="number"
                            min={0}
                            value={allocatedCount}
                            onChange={(e) => handleAllocationChange(phase, role, e.target.value)}
                            className={`
                              w-24 px-3 py-2 border rounded-md text-gray-900 bg-white
                              ${hasError ? 'border-theme-error/50 focus:ring-theme-error' : 'border-theme-border focus:ring-theme-accent'}
                              focus:ring-2 focus:border-transparent
                            `}
                          />
                          <span className="text-sm text-theme-text-muted">/ {requiredCount} required</span>
                        </div>
                        {hasError && (
                          <p className="mt-1 text-xs text-theme-error">{hasError}</p>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>

        {/* Total allocation errors */}
        {Object.entries(errors).filter(([key]) => key.startsWith('total-')).map(([key, error]) => (
          <div key={key} className="bg-theme-bg-secondary border-l-4 border-theme-error rounded-lg p-3">
            <p className="text-sm text-theme-text-secondary">{error}</p>
          </div>
        ))}

        {/* Actions */}
        <div className="flex justify-end space-x-3 pt-4 border-t">
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button 
            variant="primary" 
            onClick={handleConfirm}
            disabled={!isValid}
            className={!isValid ? 'opacity-50 cursor-not-allowed' : ''}
            title={!isValid ? 'Not enough employees to meet requirements' : ''}
          >
            Confirm Allocation
          </Button>
        </div>
      </div>
    </Modal>
  );
}